/**
 * sketchOps — pure 2D editing operations on sketch entities:
 * trim (split at intersections, drop the clicked span), single-entity
 * offset, and the polygon / slot generators. No DOM, no kernel — unit
 * testable in isolation.
 */

import type { SketchEntity, Vec2 } from '../timeline/types';

const EPS = 1e-9;
/** Merge tolerance for intersection params (mm-scale sketches). */
const TOL = 1e-6;

const sub = (a: Vec2, b: Vec2): Vec2 => [a[0] - b[0], a[1] - b[1]];
const dist = (a: Vec2, b: Vec2): number => Math.hypot(a[0] - b[0], a[1] - b[1]);

// --- intersection math -------------------------------------------------------

/** Segment × segment intersection point (proper or endpoint-touching). */
export function intersectSegSeg(a1: Vec2, a2: Vec2, b1: Vec2, b2: Vec2): Vec2[] {
  const d1 = sub(a2, a1);
  const d2 = sub(b2, b1);
  const denom = d1[0] * d2[1] - d1[1] * d2[0];
  if (Math.abs(denom) < EPS) return []; // parallel/collinear — no trim point
  const dx = b1[0] - a1[0];
  const dy = b1[1] - a1[1];
  const t = (dx * d2[1] - dy * d2[0]) / denom;
  const u = (dx * d1[1] - dy * d1[0]) / denom;
  if (t < -TOL || t > 1 + TOL || u < -TOL || u > 1 + TOL) return [];
  return [[a1[0] + t * d1[0], a1[1] + t * d1[1]]];
}

/** Segment × full circle intersection points. */
export function intersectSegCircle(a: Vec2, b: Vec2, center: Vec2, r: number): Vec2[] {
  const d = sub(b, a);
  const f = sub(a, center);
  const A = d[0] * d[0] + d[1] * d[1];
  if (A < EPS) return [];
  const B = 2 * (f[0] * d[0] + f[1] * d[1]);
  const C = f[0] * f[0] + f[1] * f[1] - r * r;
  const disc = B * B - 4 * A * C;
  if (disc < 0) return [];
  const s = Math.sqrt(disc);
  const out: Vec2[] = [];
  for (const t of disc < EPS ? [-B / (2 * A)] : [(-B - s) / (2 * A), (-B + s) / (2 * A)]) {
    if (t >= -TOL && t <= 1 + TOL) out.push([a[0] + t * d[0], a[1] + t * d[1]]);
  }
  return out;
}

/** Circle × circle intersection points. */
export function intersectCircleCircle(c1: Vec2, r1: number, c2: Vec2, r2: number): Vec2[] {
  const d = dist(c1, c2);
  if (d < EPS || d > r1 + r2 + TOL || d < Math.abs(r1 - r2) - TOL) return [];
  const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
  const h2 = r1 * r1 - a * a;
  const h = h2 > 0 ? Math.sqrt(h2) : 0;
  const mx = c1[0] + (a * (c2[0] - c1[0])) / d;
  const my = c1[1] + (a * (c2[1] - c1[1])) / d;
  const ox = (h * (c2[1] - c1[1])) / d;
  const oy = (h * (c2[0] - c1[0])) / d;
  if (h < TOL) return [[mx, my]];
  return [
    [mx + ox, my - oy],
    [mx - ox, my + oy],
  ];
}

/** An arc's angular span, normalized so end > start (CCW). */
function arcSpan(startAngle: number, endAngle: number): [number, number] {
  let e = endAngle;
  while (e <= startAngle + EPS) e += Math.PI * 2;
  return [startAngle, e];
}

/** Is angle θ inside the CCW arc span [s, e] (angles unnormalized)? */
function angleInSpan(theta: number, s: number, e: number): boolean {
  let t = theta;
  while (t < s - EPS) t += Math.PI * 2;
  return t <= e + EPS;
}

/**
 * The trimmable "curve segments" of an entity, so trim can treat rects as
 * 4 lines. Returns null for kinds trim doesn't support (spline/text/poly).
 */
export function explodeForTrim(ent: SketchEntity): SketchEntity[] | null {
  if (ent.kind === 'line' || ent.kind === 'circle' || ent.kind === 'arc') return [ent];
  if (ent.kind === 'rect') {
    const [x0, y0] = ent.min;
    const [x1, y1] = ent.max;
    return [
      { kind: 'line', a: [x0, y0], b: [x1, y0] },
      { kind: 'line', a: [x1, y0], b: [x1, y1] },
      { kind: 'line', a: [x1, y1], b: [x0, y1] },
      { kind: 'line', a: [x0, y1], b: [x0, y0] },
    ];
  }
  return null;
}

/** All intersection POINTS between a target entity and one other entity. */
export function entityIntersections(target: SketchEntity, other: SketchEntity): Vec2[] {
  const others = explodeForTrim(other);
  if (!others) return [];
  const out: Vec2[] = [];
  for (const o of others) {
    if (target.kind === 'line') {
      if (o.kind === 'line') out.push(...intersectSegSeg(target.a, target.b, o.a, o.b));
      else if (o.kind === 'circle') out.push(...intersectSegCircle(target.a, target.b, o.center, o.radius));
      else if (o.kind === 'arc') {
        const [s, e] = arcSpan(o.startAngle, o.endAngle);
        out.push(
          ...intersectSegCircle(target.a, target.b, o.center, o.radius).filter((p) =>
            angleInSpan(Math.atan2(p[1] - o.center[1], p[0] - o.center[0]), s, e),
          ),
        );
      }
    } else if (target.kind === 'circle' || target.kind === 'arc') {
      let pts: Vec2[] = [];
      if (o.kind === 'line') pts = intersectSegCircle(o.a, o.b, target.center, target.radius);
      else if (o.kind === 'circle') pts = intersectCircleCircle(target.center, target.radius, o.center, o.radius);
      else if (o.kind === 'arc') {
        const [s, e] = arcSpan(o.startAngle, o.endAngle);
        pts = intersectCircleCircle(target.center, target.radius, o.center, o.radius).filter((p) =>
          angleInSpan(Math.atan2(p[1] - o.center[1], p[0] - o.center[0]), s, e),
        );
      }
      if (target.kind === 'arc') {
        const [s, e] = arcSpan(target.startAngle, target.endAngle);
        pts = pts.filter((p) =>
          angleInSpan(Math.atan2(p[1] - target.center[1], p[0] - target.center[0]), s, e),
        );
      }
      out.push(...pts);
    }
  }
  return out;
}

/**
 * TRIM: remove the span of `entities[idx]` under the click point, cutting
 * at intersections with all OTHER entities. Returns the replacement
 * entities for idx (possibly empty = delete), or null if the entity kind
 * can't be trimmed. Rects are exploded into 4 lines first (the untouched
 * sides come back as lines).
 */
export function trimEntity(
  entities: readonly SketchEntity[],
  idx: number,
  click: Vec2,
): SketchEntity[] | null {
  const target = entities[idx];
  if (!target) return null;
  const pieces = explodeForTrim(target);
  if (!pieces) return null;

  // For rects: trim only the side nearest the click; keep the others.
  let hit = 0;
  if (pieces.length > 1) {
    let bestD = Infinity;
    pieces.forEach((p, i) => {
      if (p.kind !== 'line') return;
      const d = pointSegDistance(click, p.a, p.b);
      if (d < bestD) {
        bestD = d;
        hit = i;
      }
    });
  }
  const curve = pieces[hit]!;
  const rest = pieces.filter((_p, i) => i !== hit);

  const cuts: Vec2[] = [];
  entities.forEach((other, i) => {
    if (i === idx) return;
    cuts.push(...entityIntersections(curve, other));
  });
  // Rect sides also cut each other at the corners implicitly (endpoints).

  const trimmed = trimCurve(curve, cuts, click);
  if (trimmed === null) return null;
  return [...rest, ...trimmed];
}

/** Distance from a point to a segment. */
function pointSegDistance(p: Vec2, a: Vec2, b: Vec2): number {
  const d = sub(b, a);
  const L2 = d[0] * d[0] + d[1] * d[1];
  const t = L2 < EPS ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * d[0] + (p[1] - a[1]) * d[1]) / L2));
  return dist(p, [a[0] + t * d[0], a[1] + t * d[1]]);
}

/**
 * Count the closed loops in a bag of 2D segments [x1,y1,x2,y2] by the
 * cyclomatic number E − V + C (each disjoint simple cycle contributes 1).
 * A section of a solid box → 1; a tube (outer+inner) → 2. Endpoints are
 * quantized so tessellated arcs chain into one loop.
 */
export function countClosedLoops(segments: readonly [number, number, number, number][]): number {
  if (segments.length === 0) return 0;
  const Q = 1e3; // 0.001mm quantization for endpoint matching
  const key = (x: number, y: number): string => `${Math.round(x * Q)},${Math.round(y * Q)}`;
  const id = new Map<string, number>();
  const vertexOf = (k: string): number => {
    let v = id.get(k);
    if (v === undefined) { v = id.size; id.set(k, v); }
    return v;
  };
  const parent: number[] = [];
  const find = (a: number): number => {
    while (parent[a] !== a) { parent[a] = parent[parent[a]!]!; a = parent[a]!; }
    return a;
  };
  const ensure = (v: number): void => { if (parent[v] === undefined) parent[v] = v; };
  let edges = 0;
  for (const [x1, y1, x2, y2] of segments) {
    const a = vertexOf(key(x1, y1));
    const b = vertexOf(key(x2, y2));
    ensure(a); ensure(b);
    if (a === b) continue; // zero-length segment — not an edge
    edges++;
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  }
  const V = id.size;
  const roots = new Set<number>();
  for (let v = 0; v < V; v++) roots.add(find(v));
  const C = roots.size;
  return Math.max(0, edges - V + C);
}

/**
 * Fillet the corner where two LINES meet (shared endpoint within tol):
 * shorten both lines to the tangent points and return the connecting arc.
 * Result = { a: shortened A, b: shortened B, arc } or null (no shared
 * corner / radius too big for either segment / degenerate angle).
 */
export function filletCorner(
  lineA: SketchEntity,
  lineB: SketchEntity,
  radius: number,
  tol = 1e-6,
): { a: SketchEntity; b: SketchEntity; arc: SketchEntity } | null {
  if (lineA.kind !== 'line' || lineB.kind !== 'line' || !(radius > 0)) return null;
  // Find the shared corner and each line's far end.
  let corner: Vec2 | null = null;
  let farA: Vec2 | null = null;
  let farB: Vec2 | null = null;
  for (const [ca, fa] of [[lineA.a, lineA.b], [lineA.b, lineA.a]] as const) {
    for (const [cb, fb] of [[lineB.a, lineB.b], [lineB.b, lineB.a]] as const) {
      if (dist(ca, cb) < tol) { corner = ca; farA = fa; farB = fb; }
    }
  }
  if (!corner || !farA || !farB) return null;
  const lenA = dist(corner, farA);
  const lenB = dist(corner, farB);
  const u: Vec2 = [(farA[0] - corner[0]) / lenA, (farA[1] - corner[1]) / lenA];
  const v: Vec2 = [(farB[0] - corner[0]) / lenB, (farB[1] - corner[1]) / lenB];
  const cosT = Math.max(-1, Math.min(1, u[0] * v[0] + u[1] * v[1]));
  const theta = Math.acos(cosT);
  if (theta < 0.02 || theta > Math.PI - 0.02) return null; // (anti)parallel — no corner
  const setback = radius / Math.tan(theta / 2);
  if (setback >= lenA - tol || setback >= lenB - tol) return null; // eats a whole line
  const ta: Vec2 = [corner[0] + u[0] * setback, corner[1] + u[1] * setback];
  const tb: Vec2 = [corner[0] + v[0] * setback, corner[1] + v[1] * setback];
  // Arc centre along the angle bisector.
  const bis: Vec2 = [u[0] + v[0], u[1] + v[1]];
  const bl = Math.hypot(bis[0], bis[1]) || 1e-9;
  const cdist = radius / Math.sin(theta / 2);
  const center: Vec2 = [corner[0] + (bis[0] / bl) * cdist, corner[1] + (bis[1] / bl) * cdist];
  let a0 = Math.atan2(ta[1] - center[1], ta[0] - center[0]);
  let a1 = Math.atan2(tb[1] - center[1], tb[0] - center[0]);
  // Our arcs sweep CCW start→end; take the SHORT way around the corner.
  let span = a1 - a0;
  while (span <= 0) span += Math.PI * 2;
  if (span > Math.PI) { [a0, a1] = [a1, a0]; }
  return {
    a: { kind: 'line', a: farA, b: ta },
    b: { kind: 'line', a: farB, b: tb },
    arc: { kind: 'arc', center, radius, startAngle: a0, endAngle: a1 },
  };
}

/** Sort ascending, merging values closer than TOL (duplicate crossings). */
function dedupSorted(values: number[]): number[] {
  const sorted = [...values].sort((a, b) => a - b);
  const out: number[] = [];
  for (const v of sorted) {
    if (out.length === 0 || v - out[out.length - 1]! > TOL) out.push(v);
  }
  return out;
}

/** Trim one line/circle/arc at the given cut points, dropping the clicked span. */
function trimCurve(curve: SketchEntity, cuts: Vec2[], click: Vec2): SketchEntity[] | null {
  if (curve.kind === 'line') {
    const d = sub(curve.b, curve.a);
    const L2 = d[0] * d[0] + d[1] * d[1];
    if (L2 < EPS) return [];
    const toT = (p: Vec2): number => ((p[0] - curve.a[0]) * d[0] + (p[1] - curve.a[1]) * d[1]) / L2;
    const ts = dedupSorted(cuts.map(toT).filter((t) => t > TOL && t < 1 - TOL));
    if (ts.length === 0) return []; // nothing crosses it — trim deletes it
    const bounds = [0, ...ts, 1];
    const ct = Math.max(0, Math.min(1, toT(click)));
    let span = 0;
    for (let i = 0; i < bounds.length - 1; i++) {
      if (ct >= bounds[i]! - TOL && ct <= bounds[i + 1]! + TOL) span = i;
    }
    const out: SketchEntity[] = [];
    for (let i = 0; i < bounds.length - 1; i++) {
      if (i === span) continue;
      const p = (t: number): Vec2 => [curve.a[0] + t * d[0], curve.a[1] + t * d[1]];
      const a = p(bounds[i]!);
      const b = p(bounds[i + 1]!);
      if (dist(a, b) > TOL * 10) out.push({ kind: 'line', a, b });
    }
    return out;
  }

  if (curve.kind === 'circle') {
    const ang = (p: Vec2): number => Math.atan2(p[1] - curve.center[1], p[0] - curve.center[0]);
    const angles = dedupSorted(cuts.map(ang));
    if (angles.length < 2) return []; // 0/1 crossings can't split a circle — delete
    // Spans between consecutive cut angles, wrapping the last back to the first.
    const spans: [number, number][] = angles.map((s, i) => [
      s,
      i + 1 < angles.length ? angles[i + 1]! : angles[0]! + Math.PI * 2,
    ]);
    const ca = ang(click);
    let drop = 0;
    for (let i = 0; i < spans.length; i++) {
      if (angleInSpan(ca, spans[i]![0], spans[i]![1])) {
        drop = i;
        break;
      }
    }
    return spans
      .filter((_s, i) => i !== drop)
      .map(([s, e]) => ({
        kind: 'arc' as const,
        center: curve.center,
        radius: curve.radius,
        startAngle: s,
        endAngle: e,
      }));
  }

  if (curve.kind === 'arc') {
    const [s0, e0] = arcSpan(curve.startAngle, curve.endAngle);
    const ang = (p: Vec2): number => {
      let a = Math.atan2(p[1] - curve.center[1], p[0] - curve.center[0]);
      while (a < s0 - EPS) a += Math.PI * 2;
      return a;
    };
    const angles = dedupSorted(cuts.map(ang).filter((a) => a > s0 + TOL && a < e0 - TOL));
    if (angles.length === 0) return [];
    const bounds = [s0, ...angles, e0];
    const ca = Math.max(s0, Math.min(e0, ang(click)));
    let span = 0;
    for (let i = 0; i < bounds.length - 1; i++) {
      if (ca >= bounds[i]! - EPS && ca <= bounds[i + 1]! + EPS) span = i;
    }
    const out: SketchEntity[] = [];
    for (let i = 0; i < bounds.length - 1; i++) {
      if (i === span) continue;
      out.push({
        kind: 'arc',
        center: curve.center,
        radius: curve.radius,
        startAngle: bounds[i]!,
        endAngle: bounds[i + 1]!,
      });
    }
    return out;
  }
  return null;
}

// --- offset -------------------------------------------------------------------

/**
 * Single-entity OFFSET by distance d. Lines offset toward `side` (a point
 * indicating which side); circles/arcs grow (d>0) or shrink; rects grow
 * outward on all sides. Returns null if unsupported or degenerate.
 */
export function offsetEntity(ent: SketchEntity, d: number, side?: Vec2): SketchEntity | null {
  if (ent.kind === 'line') {
    const dir = sub(ent.b, ent.a);
    const L = Math.hypot(dir[0], dir[1]);
    if (L < EPS) return null;
    let n: Vec2 = [-dir[1] / L, dir[0] / L];
    if (side) {
      const toSide = sub(side, ent.a);
      if (toSide[0] * n[0] + toSide[1] * n[1] < 0) n = [-n[0], -n[1]];
    }
    const off = Math.abs(d);
    return {
      kind: 'line',
      a: [ent.a[0] + n[0] * off, ent.a[1] + n[1] * off],
      b: [ent.b[0] + n[0] * off, ent.b[1] + n[1] * off],
    };
  }
  if (ent.kind === 'circle') {
    const r = ent.radius + d;
    return r > TOL ? { kind: 'circle', center: ent.center, radius: r } : null;
  }
  if (ent.kind === 'ellipse') {
    const rx = ent.rx + d;
    const ry = ent.ry + d;
    return rx > TOL && ry > TOL ? { kind: 'ellipse', center: ent.center, rx, ry } : null;
  }
  if (ent.kind === 'arc') {
    const r = ent.radius + d;
    return r > TOL ? { ...ent, radius: r } : null;
  }
  if (ent.kind === 'rect') {
    const min: Vec2 = [ent.min[0] - d, ent.min[1] - d];
    const max: Vec2 = [ent.max[0] + d, ent.max[1] + d];
    return max[0] - min[0] > TOL && max[1] - min[1] > TOL ? { kind: 'rect', min, max } : null;
  }
  return null;
}

// --- generators -----------------------------------------------------------------

/** Regular N-gon inscribed in radius r, first vertex toward `toward`. */
export function polygonEntities(center: Vec2, radius: number, sides: number, toward?: Vec2): SketchEntity[] {
  const n = Math.max(3, Math.min(64, Math.round(sides)));
  const start = toward
    ? Math.atan2(toward[1] - center[1], toward[0] - center[0])
    : Math.PI / 2;
  const pts: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const a = start + (i * Math.PI * 2) / n;
    pts.push([center[0] + radius * Math.cos(a), center[1] + radius * Math.sin(a)]);
  }
  const out: SketchEntity[] = [];
  for (let i = 0; i < n; i++) out.push({ kind: 'line', a: pts[i]!, b: pts[(i + 1) % n]! });
  return out;
}

/** Rectangle with rounded corners: 4 lines + 4 quarter arcs (closed chain). */
export function roundedRectEntities(min: Vec2, max: Vec2, cornerRadius: number): SketchEntity[] | null {
  const w = max[0] - min[0];
  const h = max[1] - min[1];
  const r = cornerRadius;
  if (w <= 0 || h <= 0 || r <= 0 || r * 2 >= Math.min(w, h) - EPS) return null;
  const [x0, y0] = min;
  const [x1, y1] = max;
  const arc = (cx: number, cy: number, s: number): SketchEntity => ({
    kind: 'arc', center: [cx, cy], radius: r, startAngle: s, endAngle: s + Math.PI / 2,
  });
  return [
    { kind: 'line', a: [x0 + r, y0], b: [x1 - r, y0] },
    arc(x1 - r, y0 + r, -Math.PI / 2),
    { kind: 'line', a: [x1, y0 + r], b: [x1, y1 - r] },
    arc(x1 - r, y1 - r, 0),
    { kind: 'line', a: [x1 - r, y1], b: [x0 + r, y1] },
    arc(x0 + r, y1 - r, Math.PI / 2),
    { kind: 'line', a: [x0, y1 - r], b: [x0, y0 + r] },
    arc(x0 + r, y0 + r, Math.PI),
  ];
}

/** N-pointed star: alternating outer/inner vertices as a closed line chain. */
export function starEntities(center: Vec2, outer: number, inner: number, points: number): SketchEntity[] | null {
  const n = Math.max(3, Math.min(32, Math.round(points)));
  if (!(outer > 0) || !(inner > 0) || inner >= outer) return null;
  const pts: Vec2[] = [];
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = Math.PI / 2 + (i * Math.PI) / n;
    pts.push([center[0] + r * Math.cos(a), center[1] + r * Math.sin(a)]);
  }
  const out: SketchEntity[] = [];
  for (let i = 0; i < pts.length; i++) out.push({ kind: 'line', a: pts[i]!, b: pts[(i + 1) % pts.length]! });
  return out;
}

/**
 * Involute SPUR GEAR outline as one closed poly. Standard proportions:
 * pitch⌀ = module·teeth, addendum = m, dedendum = 1.25m, involute flanks
 * from the base circle (radial below it), root/tip arcs between teeth.
 */
export function gearEntities(
  center: Vec2,
  module: number,
  teeth: number,
  pressureAngleDeg = 20,
  rotateDeg = 0,
): SketchEntity[] | null {
  const z = Math.round(teeth);
  const m = module;
  const rot = (rotateDeg * Math.PI) / 180;
  if (!(m > 0) || z < 6 || z > 200) return null;
  const alpha = (pressureAngleDeg * Math.PI) / 180;
  const rp = (m * z) / 2;            // pitch
  const rb = rp * Math.cos(alpha);   // base
  const ra = rp + m;                 // tip (addendum)
  const rf = Math.max(rp - 1.25 * m, 0.2 * rp); // root (dedendum, clamped)
  const inv = (a: number): number => Math.tan(a) - a;
  /** Half angular tooth thickness at radius r (involute flank). */
  const gamma = (r: number): number => {
    const beta = Math.PI / (2 * z) + inv(alpha);
    if (r <= rb) return beta;                       // radial flank below base circle
    return beta - inv(Math.acos(rb / r));
  };
  const FLANK_STEPS = 6;
  const pt = (r: number, a: number): Vec2 => [
    center[0] + r * Math.cos(a + rot),
    center[1] + r * Math.sin(a + rot),
  ];

  const points: Vec2[] = [];
  const pitch = (Math.PI * 2) / z;
  for (let i = 0; i < z; i++) {
    const phi = i * pitch; // tooth center angle
    // Root arc from the gap center to the left flank base.
    const gapHalf = pitch / 2 - gamma(rf);
    points.push(pt(rf, phi - pitch / 2 + gapHalf / 2), pt(rf, phi - gamma(rf)));
    // Left flank rising rf→ra.
    for (let k = 1; k <= FLANK_STEPS; k++) {
      const r = rf + ((ra - rf) * k) / FLANK_STEPS;
      points.push(pt(r, phi - gamma(r)));
    }
    // Tip arc.
    points.push(pt(ra, phi + gamma(ra) * 0)); // tooth-center tip point keeps arcs fair
    // Right flank descending ra→rf.
    for (let k = FLANK_STEPS; k >= 1; k--) {
      const r = rf + ((ra - rf) * k) / FLANK_STEPS;
      points.push(pt(r, phi + gamma(r)));
    }
    points.push(pt(rf, phi + gamma(rf)), pt(rf, phi + pitch / 2 - gapHalf / 2));
  }
  return [{ kind: 'poly', points }];
}

/**
 * Rigid 2D transform of entities: rotate by `angle` about `about`, then
 * translate. Axis-aligned rects under rotation decompose into 4 lines.
 * Returns null if any entity can't be transformed (text).
 */
export function transformEntities(
  entities: readonly SketchEntity[],
  opts: { angle?: number; about?: Vec2; translate?: Vec2 },
): SketchEntity[] | null {
  const angle = opts.angle ?? 0;
  const [cx, cy] = opts.about ?? [0, 0];
  const [tx, ty] = opts.translate ?? [0, 0];
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const xf = (p: Vec2): Vec2 => {
    const x = p[0] - cx;
    const y = p[1] - cy;
    return [cx + x * cos - y * sin + tx, cy + x * sin + y * cos + ty];
  };
  const rotates = Math.abs(Math.sin(angle)) > EPS || cos < 0; // not a multiple of 2π
  const out: SketchEntity[] = [];
  for (const raw of entities) {
    const ent = rotates && raw.kind === 'rect' ? explodeForTrim(raw)! : [raw];
    for (const e of ent) {
      switch (e.kind) {
        case 'line': out.push({ kind: 'line', a: xf(e.a), b: xf(e.b) }); break;
        case 'rect': out.push({ kind: 'rect', min: xf(e.min), max: xf(e.max) }); break;
        case 'circle': out.push({ kind: 'circle', center: xf(e.center), radius: e.radius }); break;
        case 'ellipse':
          if (rotates) return null; // axis-aligned only — no rotated form
          out.push({ kind: 'ellipse', center: xf(e.center), rx: e.rx, ry: e.ry });
          break;
        case 'arc':
          out.push({
            kind: 'arc', center: xf(e.center), radius: e.radius,
            startAngle: e.startAngle + angle, endAngle: e.endAngle + angle,
          });
          break;
        case 'spline': out.push({ ...e, points: e.points.map(xf) }); break;
        case 'poly': out.push({ kind: 'poly', points: e.points.map(xf) }); break;
        default: return null; // text has no rotation representation
      }
    }
  }
  return out;
}

/** Obround slot: centerline a→b, overall width w (2 lines + 2 end arcs). */
export function slotEntities(a: Vec2, b: Vec2, width: number): SketchEntity[] | null {
  const d = sub(b, a);
  const L = Math.hypot(d[0], d[1]);
  const r = width / 2;
  if (L < EPS || r < EPS) return null;
  const n: Vec2 = [-d[1] / L, d[0] / L];
  const theta = Math.atan2(n[1], n[0]);
  const p = (c: Vec2, s: 1 | -1): Vec2 => [c[0] + s * n[0] * r, c[1] + s * n[1] * r];
  return [
    { kind: 'line', a: p(a, 1), b: p(b, 1) },
    { kind: 'arc', center: b, radius: r, startAngle: theta + Math.PI, endAngle: theta }, // wraps the b end
    { kind: 'line', a: p(b, -1), b: p(a, -1) },
    { kind: 'arc', center: a, radius: r, startAngle: theta, endAngle: theta + Math.PI },
  ];
}
